import React, { useEffect, useState } from 'react'; 
import { Link } from 'react-router-dom';
import useTitle from '../hooks/useTitle';
import {
  fetchTopAddresses,
  fetchSmartnodeLockedCoins,
  fetchSupply,
} from '../services/apiService';

import '../charts/chartSetup';

import {
  FaListAlt,
  FaCoins,
  FaExchangeAlt,
  FaArrowLeft,
  FaArrowRight, 
  FaLock, 
  FaEye, 
  FaQuestionCircle, 
  FaAddressBook, 
  FaUsers, 
  FaLink,
  FaFire, 
  FaUserShield, 
  FaCheckCircle, 
  FaDatabase, 
  FaAddressCard, 
  FaPercentage, 
  FaExternalLinkAlt
} from 'react-icons/fa';

import { Doughnut, Bar } from 'react-chartjs-2';

const TopAddressesPage = () => {
  useTitle("MENELIK - Holders");
  const [topAddressesData, setTopAddressesData] = useState(null);
  const [lockedCoinsData, setLockedCoinsData] = useState(null);
  const [supplyData, setSupplyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('BALANCES'); // 'BALANCES' | 'RECEIVED'

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const loadAllData = async () => {
      try {
        const [topAddRes, lockedRes, supplyRes] = await Promise.all([
          fetchTopAddresses(),
          fetchSmartnodeLockedCoins(),
          fetchSupply(),
        ]);
        setTopAddressesData(topAddRes);
        setLockedCoinsData(lockedRes);
        setSupplyData(supplyRes);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        setLoading(false);
      }
    };
    loadAllData();
  }, []);

  if (loading) {
    return <p className="text-center text-lg text-gray-500 my-8">Loading data...</p>;
  }
  if (!topAddressesData || !lockedCoinsData || !supplyData) {
    return <p className="text-center text-lg text-gray-500 my-8">Some data not found.</p>;
  }

  const { topBalances, topReceived } = topAddressesData;
  const { 
    totalLockedCoins, 
    totalUnknownLockedCoins, 
    totalKnownLockedCoins, 
    lockedAddressesCount,
    knownLockedAddressesCount,
    unknownLockedAddressesCount
  } = lockedCoinsData;
  const {
    maximumSupply,
    totalEmitted,
    burntCoins,
    developerCoins,
    circulatingSupply,
    percentages,
    burnAddress,
    developerAddress,
  } = supplyData;

  // ========= TABLA PRINCIPAL: TOP BALANCES / TOP RECEIVED =========

  const tableData = activeTab === 'BALANCES' ? topBalances : topReceived;

  // Paginación de la tabla
  const totalItems = tableData.length; 
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedData = tableData.slice(startIndex, endIndex);

  const goToPreviousPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const goToNextPage = () => setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  let totalAmount = 0;
  if (activeTab === 'BALANCES') {
    totalAmount = topBalances.reduce((acc, item) => acc + parseFloat(item.balance || 0), 0);
  } else {
    totalAmount = topReceived.reduce((acc, item) => acc + parseFloat(item.total_received || 0), 0);
  }

  const formatWithPercentage = (value) => {
    const val = parseFloat(value) || 0;
    if (!totalAmount) return val.toLocaleString();
    const pct = (val / totalAmount) * 100;
    return `${val.toLocaleString()} (${pct.toFixed(2)}%)`;
  };

  // ========= WEALTH DISTRIBUTION =========

  // el circulante (float) y el "realCirculating" (excluye lo quemado)
  const circFloat = parseFloat(circulatingSupply) || 0;
  const burnFloat = parseFloat(burntCoins) || 0;
  const realCirculating = circFloat;

  // 1) Filtrar de topBalances la dirección de quema
  const filteredBalances = topBalances.filter(
    (item) => item.address.toLowerCase() !== burnAddress.toLowerCase()
  );

  // 2) array solo de balances
  const sortedBalances = [...filteredBalances];
  sortedBalances.sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance));

  const balancesArray = sortedBalances.map((item) => parseFloat(item.balance) || 0);

  // rangos
  const sumRange = (start, end) =>
    balancesArray.slice(start, end).reduce((acc, val) => acc + val, 0);

  const sumTop1_25 = sumRange(0, 25);
  const sum26_50 = sumRange(25, 50);
  const sum51_75 = sumRange(50, 75);
  const sum76_100 = sumRange(75, 100);
  const sum1_100 = sumTop1_25 + sum26_50 + sum51_75 + sum76_100;
  const sum101_200 = sumRange(100, 200);
  const sum1_200 = sum1_100 + sum101_200;

  // "Top 201+" = realCirculating - sum1_200
  const sum201plus = Math.max(realCirculating - sum1_200, 0);

  // Porcentaje respecto al *real* circulante
  const percentageOfRealCirc = (val) => {
    if (realCirculating === 0) return 0;
    return (val / realCirculating) * 100;
  };

  //  distribución
  const wealthDistribution = [
    {
      range: 'Top 1-25',
      amount: sumTop1_25,
      percentage: percentageOfRealCirc(sumTop1_25).toFixed(2),
    },
    {
      range: 'Top 26-50',
      amount: sum26_50,
      percentage: percentageOfRealCirc(sum26_50).toFixed(2),
    },
    {
      range: 'Top 51-75',
      amount: sum51_75,
      percentage: percentageOfRealCirc(sum51_75).toFixed(2),
    },
    {
      range: 'Top 76-100',
      amount: sum76_100,
      percentage: percentageOfRealCirc(sum76_100).toFixed(2),
    },
    {
      range: 'Top 1-100 Total',
      amount: sum1_100,
      percentage: percentageOfRealCirc(sum1_100).toFixed(2),
    },
    {
      range: 'Top 101-200',
      amount: sum101_200,
      percentage: percentageOfRealCirc(sum101_200).toFixed(2),
    },
    {
      range: 'Top 1-200 Total',
      amount: sum1_200,
      percentage: percentageOfRealCirc(sum1_200).toFixed(2),
    },
    {
      range: 'Top 201+',
      amount: sum201plus,
      percentage: percentageOfRealCirc(sum201plus).toFixed(2),
    },
    {
      range: 'Circulating (Incl. Dev)',
      amount: realCirculating,
      percentage: '100.00',
    },

    {
      range: `Burn Address (${burnAddress.slice(0, 6)}...${burnAddress.slice(-6)})`,
      amount: burnFloat,
      percentage: ((burnFloat / circFloat) * 100).toFixed(2),
    },
  ];

  // ========= GRAFICA #1: BARRAS (Max, Circulating, Burn, Locked & Developer) =========

  const barData = {
    labels: [
      'Max Supply',
      'Circulating',
      'Burn',
      'Locked',
      'Developer',
    ],
    datasets: [
      {
        label: 'Coins',
        data: [
          maximumSupply,
          realCirculating,
          burnFloat,
          parseFloat(totalLockedCoins),
          parseFloat(developerCoins),
        ],
        backgroundColor: [
          '#6366f1', // Max
          '#10b981',
          '#ef4444',
          '#f59e0b',
          '#8b5cf6',
        ],
      },
    ],
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
    },
    scales: {
      x: { beginAtZero: true },
      y: { beginAtZero: true },
    },
  };

  // ========= GRAFICA #2: DONUT (Supply Distribution) =========
  const parsePct = (pctStr) => parseFloat(pctStr.replace('%', '')) || 0;
  const burntPct = parsePct(percentages.burnt);
  const pendingPct = parsePct(percentages.pendingToEmit);
  const emittedPct = parsePct(percentages.emitted);
  const devPct = (parseFloat(developerCoins) / maximumSupply) * 100 || 0;

  // developerCoins ya está incluido dentro de "emittedPct":
  const emittedExclDev = Math.max(emittedPct - devPct, 0);

  const donutSupplyData = {
    labels: ['Emitted (excl. dev)', 'Developer', 'Burnt', 'Pending'],
    datasets: [
      {
        label: 'Supply Distribution',
        data: [emittedExclDev, devPct, burntPct, pendingPct],
        backgroundColor: ['#3b82f6', '#8b5cf6', '#f87171', '#f59e0b'],
        hoverBackgroundColor: ['#1e3a8a', '#7c3aed', '#dc2626', '#d97706'],
      },
    ],
  };

  const donutSupplyOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
    },
  };

  // ========= GRAFICA #3: DONUT (Wealth Distribution) =========
  // Solo rangos principales (excluyendo la Burn Address)
  const donutWealthItems = wealthDistribution.filter((wd) =>
    [
      'Top 1-25',
      'Top 26-50',
      'Top 51-75',
      'Top 76-100',
      'Top 101-200',
      'Top 201+',
    ].includes(wd.range)
  );

  const donutWealthData = {
    labels: donutWealthItems.map((item) => item.range),
    datasets: [
      {
        label: 'Wealth Distribution',
        data: donutWealthItems.map((item) => item.amount),
        backgroundColor: [
          '#2563eb',
          '#10b981',
          '#f59e0b',
          '#ef4444',
          '#6366f1',
          '#14b8a6',
        ],
        hoverBackgroundColor: [
          '#1e40af',
          '#059669',
          '#d97706',
          '#dc2626',
          '#4f46e5',
          '#0d9488',
        ],
      },
    ],
  };

  const donutWealthOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6 flex items-center text-gray-800">
        <FaListAlt className="mr-2 text-blue-600" />
        Holders Top Addresses + Supply Info
      </h1>

      {/* SECTION 1: SMARTNODE LOCKED COINS + SUPPLY CARDS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">

        <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
            <FaDatabase className="mr-2 text-blue-500" /> Supply Info
          </h2>

          <div className="relative group">
            <p className="text-gray-600 flex items-center">
              <FaCheckCircle className="mr-2 text-green-500" />
              <strong>Circulating Supply (Incl. Locked):</strong> {parseFloat(circFloat - totalLockedCoins).toLocaleString()}
            </p>
            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-64 bg-gray-800 text-white text-xs rounded p-2 shadow-lg">
              Locked coins have been deducted from the circulating supply.
            </div>
          </div>

          <p className="text-gray-600 flex items-center">
            <FaFire className="mr-2 text-red-500" />
            <strong>Burnt Coins:</strong> {burnFloat.toLocaleString()}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaUserShield className="mr-2 text-purple-500" />
            <strong>Developer Coins:</strong> {parseFloat(developerCoins).toLocaleString()}
          </p>

          <div className="relative group">
            <p className="text-gray-600 flex items-center">
              <FaCheckCircle className="mr-2 text-green-500" />
              <strong>Circulating Supply (Incl. Dev):</strong> {circFloat.toLocaleString()}
            </p>
            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block w-64 bg-gray-800 text-white text-xs rounded p-2 shadow-lg">
              Coins intended for development have been deducted from the circulating supply.
            </div>
          </div>

          <p className="text-gray-600 flex items-center">
            <FaDatabase className="mr-2 text-blue-500" />
            <strong>Total Emitted:</strong> {totalEmitted.toLocaleString()}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaCoins className="mr-2 text-yellow-500" />
            <strong>Maximum Supply:</strong> {maximumSupply.toLocaleString()}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaAddressCard className="mr-2 text-gray-600" />
            <strong>Burn Address:</strong>{' '}
            <a
              href={`/address/${burnAddress}`}
              className="text-blue-500 hover:underline flex items-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              {`${burnAddress.slice(0, 4)}...${burnAddress.slice(-4)}`} 
              <FaExternalLinkAlt className="ml-1" />
            </a>
          </p>

          <p className="text-gray-600 flex items-center">
            <FaAddressCard className="mr-2 text-gray-600" />
            <strong>Developer Address:</strong>{' '}
            <a
              href={`/address/${developerAddress}`}
              className="text-blue-500 hover:underline flex items-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              {`${developerAddress.slice(0, 4)}...${developerAddress.slice(-4)}`} 
              <FaExternalLinkAlt className="ml-1" />
            </a>
          </p>

          <div className="mt-3 text-sm text-gray-500">
            <p className="flex items-center">
              <FaPercentage className="mr-2 text-red-500" />
              <strong>Percent Burnt:</strong> {percentages.burnt}
            </p>

            <p className="flex items-center">
              <FaPercentage className="mr-2 text-green-500" />
              <strong>Percent Emitted:</strong> {percentages.emitted}
            </p>

            <p className="flex items-center">
              <FaPercentage className="mr-2 text-orange-500" />
              <strong>Percent Pending:</strong> {percentages.pendingToEmit}
            </p>
          </div>
        </div>

        {/* CARD: SMARTNODE LOCKED COINS */}
        <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center">
            <FaLock className="mr-2 text-blue-500" /> Smartnode Locked Coins
          </h2>
          
          <p className="text-gray-600 flex items-center">
            <FaCoins className="mr-2 text-yellow-500" />
            <strong>Total Locked Coins:</strong>{' '}
            {parseFloat(totalLockedCoins).toLocaleString()}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaEye className="mr-2 text-green-500" />
            <strong>Known Locked:</strong>{' '}
            {totalKnownLockedCoins}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaQuestionCircle className="mr-2 text-red-500" />
            <strong>Unknown Locked:</strong>{' '}
            {totalUnknownLockedCoins}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaAddressBook className="mr-2 text-blue-500" />
            <strong>Known Locked Addresses:</strong>{' '}
            {knownLockedAddressesCount}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaUsers className="mr-2 text-gray-600" />
            <strong>Unknown Locked Addresses:</strong>{' '}
            {unknownLockedAddressesCount}
          </p>

          <p className="text-gray-600 flex items-center">
            <FaLock className="mr-2 text-purple-500" />
            <strong>Locked Addresses Count:</strong>{' '}
            {lockedAddressesCount}
          </p>

          <div className="mt-4">
            <a
              href="/locked-coins"
              className="bg-blue-500 text-white px-4 py-2 rounded-lg shadow hover:bg-blue-600 transition duration-300 inline-flex items-center"
            >
              <FaLock className="mr-2" /> View Locked Coins
            </a>
          </div>
        </div>

      </div>

      {/* SECTION 2: Dos gráficas (Barras + Donut Supply) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        
        {/* GRAFICA DE BARRAS */}
        <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Max, Circulating, Burn, Locked & Developer
          </h3>
          <div className="h-72">
            <Bar data={barData} options={barOptions} />
          </div>
        </div>

        {/* GRAFICA DONUT SUPPLY */}
        <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">
            Supply Distribution (%)
          </h3>
          <div className="h-72">
            <Doughnut data={donutSupplyData} options={donutSupplyOptions} />
          </div>
        </div>
      </div>

      {/* SECTION 3: TABLAS y GRAFICO DE DISTRIBUCION DE RIQUEZA */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* TABLA: TOP BALANCES / TOP RECEIVED */}
        <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <div className="flex space-x-4 mb-4">
            <button
              onClick={() => {
                setActiveTab('BALANCES');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded font-medium flex items-center ${
                activeTab === 'BALANCES'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              <FaCoins className="mr-2" />
              Top Balances
            </button>
            <button
              onClick={() => {
                setActiveTab('RECEIVED');
                setCurrentPage(1);
              }}
              className={`px-4 py-2 rounded font-medium flex items-center ${
                activeTab === 'RECEIVED'
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-100 text-blue-700'
              }`}
            >
              <FaExchangeAlt className="mr-2" />
              Top Received
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="table-auto w-full min-w-[600px] bg-gray-50 rounded">
              <thead>
                <tr className="bg-gray-200 text-gray-700">
                  <th className="p-3 border-b">#</th>
                  <th className="p-3 border-b">Address</th>
                  <th className="p-3 border-b">
                    {activeTab === 'BALANCES' ? 'Balance' : 'Total Received'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => {
                  const globalIndex = startIndex + index + 1;
                  return (
                    <tr key={index} className="hover:bg-gray-100">
                      <td className="p-3 border-b text-center">{globalIndex}</td>
                      <td className="p-3 border-b break-all">
                        <Link
                          to={`/address/${item.address}`}
                          className="text-blue-600 hover:underline"
                        >
                          {item.address}
                        </Link>
                        {item.display_name && ( 
                          item.url ? (
                            <a
                              href={item.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="ml-2 px-2 py-0 text-xs font-semibold text-white bg-blue-500 rounded-full shadow inline-flex items-center hover:bg-blue-600 transition"
                            >
                              {item.display_name}
                              <FaLink className="ml-1" />
                            </a>
                          ) : (
                            <span className="ml-2 px-2 py-0 text-xs font-semibold text-white bg-blue-500 rounded-full shadow inline-flex">
                              {item.display_name}
                            </span>
                          )
                        )}

                      </td>
                      <td className="p-3 border-b">
                        {activeTab === 'BALANCES'
                          ? formatWithPercentage(item.balance)
                          : formatWithPercentage(item.total_received)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Paginador */}
          <div className="flex justify-between items-center mt-4">
            <button
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded disabled:opacity-50 flex items-center"
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
            >
              <FaArrowLeft className="mr-2" />
              Previous
            </button>

            <p className="text-sm text-gray-700">
              Page {currentPage} of {totalPages}
            </p>

            <button
              className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded disabled:opacity-50 flex items-center"
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
            >
              Next
              <FaArrowRight className="ml-2" />
            </button>
          </div>
        </div>

        {/* TABLA + GRAFICO: WEALTH DISTRIBUTION (excl. Burn) */}
        <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">Wealth Distribution</h2>

          <div className="overflow-x-auto">
            <table className="table-auto w-full min-w-[450px] bg-gray-50 rounded">
              <thead>
                <tr className="bg-gray-200 text-gray-700">
                  <th className="p-3 border-b">Amount Range</th>
                  <th className="p-3 border-b">Amount</th>
                  <th className="p-3 border-b">% of Circulating</th>
                </tr>
              </thead>
              <tbody>
                {wealthDistribution.map((wd, idx) => (
                  <tr key={idx} className="hover:bg-gray-100">
                    <td className="p-3 border-b">{wd.range}</td>
                    <td className="p-3 border-b">
                      {wd.amount.toLocaleString(undefined, {
                        maximumFractionDigits: 8,
                      })}
                    </td>
                    <td className="p-3 border-b">{wd.percentage}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="h-72 mt-6">
            <Doughnut data={donutWealthData} options={donutWealthOptions} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopAddressesPage;

